/**
 * SF-156: mobile Dossier Effects-tab filter — live-derived option set (category options + one per
 * distinct active `effectType` in the kingdom) and the effective-selection/row-matching logic.
 * Threaded as page-derived data (`effectFilterOptions` prop) rather than re-derived independently
 * inside the shell, per Application Interface's State ownership section.
 */

import { classifyEffect } from './effectClassification';
import { formatEnumLabel } from './format';
import type { DossierProvinceRow } from '../types/dossier';
import type { CrystalBallData } from '../types/intel';

export type EffectFilterGroup = 'category' | 'specific';

export interface EffectFilterOption {
  value: string;
  label: string;
  group: EffectFilterGroup;
}

function activeEffects(row: DossierProvinceRow) {
  return (row.latestCb?.data as CrystalBallData | undefined)?.activeEffects ?? [];
}

/**
 * Category options (Any Defense/Debuff/Buff) + one option per distinct `effectType` currently
 * active anywhere in the kingdom, sourced live from `provinces`. Does NOT include the bare
 * "Any effect" leading option — that's a fixed sentinel (`null`) rendered directly by the shell,
 * not part of this live-derived set.
 */
export function deriveEffectFilterOptions(provinces: DossierProvinceRow[]): EffectFilterOption[] {
  const categoryOptions: EffectFilterOption[] = [
    { value: 'DEFENSE', label: 'Any Defense', group: 'category' },
    { value: 'DEBUFF', label: 'Any Debuff', group: 'category' },
    { value: 'BUFF', label: 'Any Buff', group: 'category' },
  ];

  const seen = new Set<string>();
  provinces.forEach((row) => activeEffects(row).forEach((e) => seen.add(e.effectType)));

  const specificOptions: EffectFilterOption[] = [...seen].sort().map((effectType) => ({
    value: effectType,
    label: formatEnumLabel(effectType),
    group: 'specific',
  }));

  return [...categoryOptions, ...specificOptions];
}

/**
 * Resolves the raw stored filter selection against the live option set — if a `refresh()`
 * changed the kingdom's active-effect set out from under a previously-valid selection, the
 * effective value resolves to `null` ("All") rather than a dangling controlled-`<select>` value.
 */
export function resolveEffectiveFilter(raw: string | null, options: EffectFilterOption[]): string | null {
  if (raw == null) return null;
  return options.some((o) => o.value === raw) ? raw : null;
}

/** True iff `row` matches the given filter value (`null` = show all). */
export function matchesEffectFilter(row: DossierProvinceRow, filter: string | null): boolean {
  if (filter == null) return true;
  const effects = activeEffects(row);
  if (filter === 'DEFENSE' || filter === 'DEBUFF' || filter === 'BUFF') {
    return effects.some((e) => classifyEffect(e.effectType) === filter);
  }
  return effects.some((e) => e.effectType === filter);
}
